import React, { useEffect, useState } from 'react';
import { signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../../firebaseConfig';
import { clearClientWorkspaceData } from '../common/clientWorkspaceData';
import { MFA_LOGIN_NOTICE, mfaError } from '../common/mfa';
import { requestRecentMfa } from './mfaDialogs';
import { mfaStyles as styles } from './mfaStyles';

type PendingChange = { requestId: string; email: string; expiresAt: number; resendAfter: number };

export default function EmailChangeSection({ disabled = false, onBusyChange }: {
    disabled?: boolean;
    onBusyChange: (busy: boolean) => void;
}) {
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [pending, setPending] = useState<PendingChange | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [now, setNow] = useState(Date.now());
    useEffect(() => { onBusyChange(busy); }, [busy, onBusyChange]);
    useEffect(() => {
        if (!pending)
            return;
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [pending]);

    const change = async (action: () => Promise<void>) => {
        if (busy || disabled)
            return;
        setBusy(true);
        setError('');
        try {
            await action();
        } catch (error) {
            setError(mfaError(error));
        } finally {
            setBusy(false);
        }
    };
    const send = (event: React.FormEvent) => {
        event.preventDefault();
        void change(async () => {
            await requestRecentMfa();
            const result = await httpsCallable<unknown, PendingChange>(functions, 'manageOwnEmail')({ action: 'request', email: pending?.email || email });
            setPending(result.data);
            setCode('');
            setNow(Date.now());
        });
    };
    const verify = (event: React.FormEvent) => {
        event.preventDefault();
        void change(async () => {
            const result = await httpsCallable<unknown, { email: string }>(functions, 'manageOwnEmail')({ action: 'confirm', requestId: pending?.requestId, code });
            sessionStorage.setItem(MFA_LOGIN_NOTICE, `Email changed to ${result.data.email}. Sign in with that email and your existing password and MFA.`);
            clearClientWorkspaceData();
            await signOut(auth);
        });
    };
    const cancel = () => void change(async () => {
        await httpsCallable(functions, 'manageOwnEmail')({ action: 'cancel' });
        setPending(null);
        setCode('');
    });
    const blocked = busy || disabled;
    const resendSeconds = pending ? Math.max(0, Math.ceil((pending.resendAfter - now) / 1000)) : 0;
    return <section className="space-y-4 border-t border-slate-200 pt-5" aria-label="Change email">
        <h2 className="text-lg font-bold">Change email</h2>
        <p className={styles.text}>Current email: {auth.currentUser?.email}</p>
        <p className={styles.text}>Verify your new email before changing your sign-in address. Your password, MFA methods and permissions stay the same. Sign in again after the change.</p>
        {error && <p role="alert" className={styles.error}>{error}</p>}
        {!pending ? <form onSubmit={send} className="space-y-4">
            <label className="block text-sm font-semibold">New email<input type="email" autoComplete="email" maxLength={320} required value={email} disabled={blocked} onChange={event => setEmail(event.target.value)} className={styles.input} /></label>
            <button className={styles.button} disabled={blocked}>{busy ? 'Sending…' : 'Verify new email'}</button>
        </form> : <>
            <p role="status" className={styles.notice}>Code sent to {pending.email}. {now >= pending.expiresAt ? 'Code expired. Request another code.' : 'Code expires in 10 minutes.'}</p>
            <form onSubmit={verify} className="space-y-4">
                <label className="block text-sm font-semibold">Email code<input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} disabled={blocked} onChange={event => setCode(event.target.value.replace(/\D/g, ''))} className={styles.input} /></label>
                <button className={styles.button} disabled={blocked || !/^\d{6}$/.test(code) || now >= pending.expiresAt}>{busy ? 'Saving…' : 'Change email and sign out'}</button>
            </form>
            <div className="flex flex-wrap gap-3"><button type="button" className={styles.secondary} disabled={blocked || resendSeconds > 0} onClick={send}>{resendSeconds ? `Resend in ${resendSeconds}s` : 'Resend code'}</button>
                <button type="button" className={styles.secondary} disabled={blocked} onClick={cancel}>Cancel email change</button></div>
        </>}
    </section>;
}
